import React, { useState, useEffect } from "react";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ErrorModal from './ErrorModal';

const BoardCurrencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [deleteState, setDeleteState] = useState(false);
  const [errors, setErrors] = useState({});
  const [apierrors, setApiErrors] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterTerm, setFilterTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(' Lade, bitte warten...');
  const [showModal, setShowModal] = useState(false);
  const [errorCode, setApiErrorCode] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    currency_id: '',
    currency_officiel_code: '',
    currency_name: ''
  });

  // KORRIGIERTE VALIDIERUNGSFUNKTION
  const validateForm = () => {
    const newErrors = {};
    // Prüft, ob das Feld existiert UND ob es nach dem Trimmen leer ist.
    if (!formData.currency_name || !formData.currency_name.trim()) {
      newErrors.currency_name = 'Währungsname ist ein Pflichtfeld';
    }
    if (!formData.currency_officiel_code || !formData.currency_officiel_code.trim()) {
      newErrors.currency_officiel_code = 'Offizieller Währungscode ist ein Pflichtfeld';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    UserService.getCurrencyBoard().then(
      (response) => {
        setCurrencies(response.data);
      },
      (error) => {
        const _content =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();

        if (error.response) {
            setApiErrors(`Error: ${error.name} - ${error.response.data.message}-${error.code}`);
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
        } else if (error.request) {
            setApiErrors('Error: No response received from the server.');
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
        } else {
            setApiErrors(`Error: ${error.message}`);
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
        } 
        
        // setCurrencies(_content); // This line could cause issues if _content is just an error string

        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        }
      }
    );
  }, [deleteState]);

  const filteredCurrencies = Array.isArray(currencies)
    ? currencies.filter((currency) =>
        (currency.currency_name && currency.currency_name.toLowerCase().includes(filterTerm.toLowerCase())) ||
        (currency.currency_officiel_code && currency.currency_officiel_code.toLowerCase().includes(filterTerm.toLowerCase()))
      )
    : [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCurrencies = [...filteredCurrencies].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const currentCurrencies = sortedCurrencies.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCurrencies.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEditClick = (currency) => {
    setApiErrors('');
    setApiErrorCode('');
    setSelectedCurrency(currency);
    setFormData(currency);
    setShowEditModal(true);
  };

  // KORRIGIERTE CREATE-FUNKTION
  const handleCreateClick = () => {
    setApiErrors('');
    setApiErrorCode('');
    setSelectedCurrency(null);
    // Initialisiert das Formular mit leeren Strings, um 'undefined' Fehler zu vermeiden
    setFormData({
      currency_officiel_code: '',
      currency_name: ''
    });
    setShowCreateModal(true);
  };

  const handleDeleteClick = async (id, e) => {
    e.preventDefault();
    setApiErrors('');
    setApiErrorCode('');
    try {
      setLoadingText('Daten werden gelöscht, bitte warten...');
      setLoading(true);
      await UserService.deleteCurrencyBoard(id);
      setDeleteState(prev => !prev);
    } catch (error) {
      setApiErrors(`Error: ${error.name}-${error.code} - ${error.response.data.message}-${error.response.data.error}`);
      setApiErrorCode(`Error: ${error.name} - ${error.code}`);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
        if (selectedCurrency) { // Edit Mode
            await UserService.updateCurrencyBoard(selectedCurrency.currency_id, formData);
            setShowEditModal(false);
        } else { // Create Mode
            await UserService.createCurrencyBoard(formData);
            setShowCreateModal(false);
        }
        setDeleteState(prev => !prev); // Refresh list
    } catch (error) {
        const errorMsg = error.response ? error.response.data.message : error.message;
        setApiErrors(`Fehler: ${errorMsg}`);
        setApiErrorCode(`Code: ${error.code || 'N/A'}`);
        setShowModal(true);
    } finally {
        setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Validierung beim Verlassen des Feldes und bei jeder Eingabe
  useEffect(() => {
    validateForm();
  }, [formData]);

  const handleCloseModal = () => setShowModal(false);

  return (
    <Container>
      <h1 className="mt-5">Währungsübersicht</h1>
      <ErrorModal show={showModal} handleClose={handleCloseModal} errorCode={errorCode} errorMessage={apierrors} />
      <Button variant="info" onClick={handleCreateClick} className="mr-2 mb-2">Erstellen</Button>
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}

      <Form.Group controlId="formBasicSearch">
        <Form.Control
          type="text"
          placeholder="Suche nach Währungsname, Code..."
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
        />
      </Form.Group>
      <Table striped bordered hover className="mt-4">
        <thead>
          <tr>
            <th onClick={() => handleSort('currency_id')} style={{ cursor: 'pointer' }}>ID {sortConfig.key === 'currency_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
            <th onClick={() => handleSort('currency_officiel_code')} style={{ cursor: 'pointer' }}>Offizieller Währungscode {sortConfig.key === 'currency_officiel_code' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
            <th onClick={() => handleSort('currency_name')} style={{ cursor: 'pointer' }}>Währungsname {sortConfig.key === 'currency_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {currentCurrencies.map((currency) => (
            <tr key={currency.currency_id}>
              <td>{currency.currency_id}</td>
              <td>{currency.currency_officiel_code}</td>
              <td>{currency.currency_name}</td>
              <td>
                <ButtonGroup>
                  <Button variant="warning" onClick={() => handleEditClick(currency)} className="mr-2" title="Bearbeiten" aria-label="Bearbeiten"><FaEdit /></Button>
                  <Button variant="danger" onClick={(e) => handleDeleteClick(currency.currency_id, e)} title="Löschen" aria-label="Löschen"><FaTrash /></Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination>
        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>{index + 1}</Pagination.Item>
        ))}
        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>

      {/* Create & Edit Modals */}
      <Modal show={showCreateModal || showEditModal} onHide={() => { setShowCreateModal(false); setShowEditModal(false); }}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedCurrency ? 'Währung bearbeiten' : 'Neue Währung erstellen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="currency_officiel_code">
              <Form.Label>Offizieller Währungscode</Form.Label>
              <Form.Control
                type="text"
                placeholder="Offiziellen Währungscode eingeben"
                name="currency_officiel_code"
                value={formData.currency_officiel_code || ''}
                onChange={handleChange}
                isInvalid={!!errors.currency_officiel_code}
              />
              <Form.Control.Feedback type="invalid">{errors.currency_officiel_code}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="currency_name">
              <Form.Label>Währungsname</Form.Label>
              <Form.Control
                type="text"
                placeholder="Währungsnamen eingeben"
                name="currency_name"
                value={formData.currency_name || ''}
                onChange={handleChange}
                isInvalid={!!errors.currency_name}
              />
              <Form.Control.Feedback type="invalid">{errors.currency_name}</Form.Control.Feedback>
            </Form.Group>
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {loading ? (selectedCurrency ? 'Wird aktualisiert...' : 'Wird gespeichert...') : (selectedCurrency ? 'Währung aktualisieren' : 'Währung speichern')}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BoardCurrencies;